<template>
  <div class="flex justify-between items-end border-b border-royal-blue-20 pb-4 mb-8">
    <h2 class="font-roboto text-2xl font-bold ">
      Add authors
    </h2>
    <IngestedBadge
      v-if="hasExtractedAuthors"
      id="ingested-badge-authors"
    />
  </div>

  <InlineWarningMessage
    v-if="props.showValidationErrors && authors.length === 0"
    errorTestId="no-authors"
    :errorMessage="'At least one author must be provided'"
    class="mb-8"
  />
  <draggable
    v-model="authors"
    item-key="id"
    tag="ul"
    class="space-y-4 list-none p-0 mb-8"
    :disabled="!reorderEnabled">
    <template #item="{ element: author, index }">
      <AuthorListItem
        :key="author.id"
        :id="author.id"
        :firstName="author.firstName"
        :initials="author.initials"
        :lastName="author.lastName"
        :isCorresponding="author.isCorresponding"
        :email="author.email"
        :orcid="author.orcid"
        :affiliation="author.affiliation"
        :index="index"
        :showCorrespondingRadio="true"
        :showDrag="reorderEnabled">
      </AuthorListItem>
    </template>
  </draggable>
  <EditAuthor
    v-if="create"
    @saved="storeNewAuthor"
    @cancel="cancelCreate">
  </EditAuthor>
  <div class="flex flex-col gap-4 sm:flex-row sm:gap-4 items-start mt-8 mb-20">
    <ButtonComponent
      label="Add author"
      variant="primary"
      id="addAuthor"
      data-testid="add-author-button"
      @customClick="addAuthor"
      v-if="canAddMoreAuthors"
    />
    <ButtonComponent
      label="Remove all authors"
      variant="secondary"
      id="removeAuthor"
      @customClick="removeAllAuthors"
      v-if="canRemoveAllAuthors"
      data-testid="remove-all-authors-btn"
    />
  </div>
  <DialogComponent
    data-testid="remove-all-authors-confirmation-dialog"
    v-model="showConfirmationDialog"
    v-slot="{ close, cancel }"
    class="w-[580px] sm:w-[480px] sm:min-h-[220px] p-6">
    <h2 class="text-2xl font-bold mt-6 mb-6 max-w-xs">
      Are you sure you want to remove all authors?
    </h2>

    <p class="mb-6">
      This action can't be undone.
    </p>
    <div class="flex gap-2">
      <ButtonComponent
        data-testid="remove-all-authors-confirmation"
        label="Remove all authors"
        @custom-click="() => { removeAllAuthorsDecision = true; close(); }"
        autofocus
      />
      <ButtonComponent
        data-testid="remove-all-authors-cancel"
        variant="tertiary"
        label="Cancel"
        class="bg-transparent text-royal-blue-100 rounded-sm relative hover:text-royal-blue-160 focus:text-science-blue-100 underline"
        @custom-click="() => { removeAllAuthorsDecision = false; cancel(); }"
      />
    </div>
  </DialogComponent>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Author } from '@/types'
import EditAuthor from './EditAuthor.vue'
import AuthorListItem from './AuthorListItem.vue'
import { useStore } from 'vuex'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import DialogComponent from '../ui-components/DialogComponent.vue'
import draggable from 'vuedraggable'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import IngestedBadge from '@/components/common/IngestedBadge.vue'
import { useAuthorManagement } from '@/composables/useAuthorManagement'

const props = defineProps<{
  showValidationErrors: boolean
}>()

const { authors, addAuthor: addNewAuthor, clearAllAuthors } = useAuthorManagement()

const store = useStore()
const removeAllAuthorsDecision = ref(false)
const showConfirmationDialog = ref(false)
const create = ref(false)

const reorderEnabled = computed(() => authors.value.length > 1)

const canAddMoreAuthors = computed((): boolean => {
  return authors.value.length < 99
})

const canRemoveAllAuthors = computed((): boolean => {
  return 0 < authors.value.length
})

async function addAuthor() {
  create.value = true
}

const hasExtractedAuthors = computed(() => {
  const snap = store.state.extractedSnapshot || {}
  return !!snap.authors
})

function storeNewAuthor(author: Author) {
  addNewAuthor(author).then(() => cancelCreate())
}

function cancelCreate() {
  create.value = false
}

function removeAllAuthors() {
  showConfirmationDialog.value = true
}

watch(removeAllAuthorsDecision, (newVal) => {
  if (newVal) {
    clearAllAuthors()
    removeAllAuthorsDecision.value = false
  }
})

watch(authors, () => {
  const snap = store.state.extractedSnapshot || {}
  if (snap.authors) store.commit('_invalidateExtractedSnapshotField', 'authors')
}, { deep: true })

</script>
